import React from 'react';
import { useRAGStore } from '../store/ragStore';
import { FolderOpen, FileText, Shield, Calendar, BarChart3, Edit3 } from 'lucide-react';

const ContextManagementTab: React.FC = () => {
  const { currentContext, setContextualRetrieval, retrievalParams, setRetrievalParams } = useRAGStore();
  const [contextualBusy, setContextualBusy] = React.useState(false);
  const [contextualProgress, setContextualProgress] = React.useState<{ done: number; total: number } | null>(null);
  // Toggling always re-embeds the whole context (minutes of LLM-billed work), so
  // gate it behind a confirm dialog instead of firing on the raw click.
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const fileCount = currentContext?.ragItems?.length || 0;
  const nextEnabled = !currentContext?.contextualRetrieval;

  const runToggle = async () => {
    if (!currentContext) return;
    const next = !currentContext.contextualRetrieval;
    setConfirmOpen(false);
    setContextualBusy(true);
    setContextualProgress(null);
    try {
      // reembed=true so the change is applied to existing files immediately;
      // onProgress reports re-embed task completion (done/total) so the toggle
      // reflects the real ~minutes-long re-embed, not just the enqueue call.
      await setContextualRetrieval(next, true, (done, total) => setContextualProgress({ done, total }));
    } finally {
      setContextualBusy(false);
      setContextualProgress(null);
    }
  };

  const handleToggleContextual = () => {
    if (!currentContext || contextualBusy) return;
    setConfirmOpen(true);
  };

  if (!currentContext) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="text-center py-12">
            <FolderOpen className="h-16 w-16 text-blue-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              No Context Selected
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Please select a context from the left sidebar to view its management options and details.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
              <h4 className="font-medium text-blue-900 mb-2">How to get started:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Select a context from the left sidebar</li>
                <li>• Create a new context if none exist</li>
                <li>• View context details and statistics</li>
                <li>• Manage context configuration</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Context Header */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-full">
            <FolderOpen className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentContext.contextId}</h2>
            <p className="text-gray-600">Context Management Dashboard</p>
          </div>
        </div>
      </div>

                                           {/* Quick Stats */}
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                  <div className="card p-5">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-full">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">Total Files</p>
                <p className="text-xl font-bold text-gray-900">{currentContext.ragItems?.length || 0}</p>
              </div>
            </div>
          </div>

                  <div className="card p-5">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">Protected Keywords</p>
                <p className="text-xl font-bold text-gray-900">{currentContext.protectedKeywords?.length || 0}</p>
              </div>
            </div>
          </div>

                                    <div className="card p-5">
             <div className="flex items-center space-x-3">
               <div className="p-2 bg-purple-100 rounded-full">
                 <Calendar className="h-5 w-5 text-purple-600" />
               </div>
               <div className="min-w-0 flex-1">
                 <p className="text-sm font-medium text-gray-600">Last Updated</p>
                 <p className="text-sm font-semibold text-gray-900 leading-tight">
                   {new Date(currentContext.updatedAt).toLocaleString()}
                 </p>
               </div>
             </div>
           </div>

                                                                         <div className="card p-5">
              <div className="flex items-center space-x-3">
                {(() => {
                  const hasValidItems = currentContext.ragItems?.some(item => item.status?.toLowerCase() === 'valid');
                  return (
                    <>
                      <div className={`p-2 rounded-full ${
                        hasValidItems ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <BarChart3 className={`h-5 w-5 ${
                          hasValidItems ? 'text-green-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-600">Status</p>
                        <p className={`text-sm font-semibold ${
                          hasValidItems ? 'text-green-600' : 'text-gray-600'
                        }`}>
                          {hasValidItems ? 'Available' : 'Unavailable'}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
       </div>

      {/* Context Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Context ID</p>
              <p className="text-gray-900 font-mono bg-gray-50 p-2 rounded">{currentContext.contextId}</p>
            </div>
            
                         <div>
               <p className="text-sm font-medium text-gray-700">Created At</p>
               <p className="text-gray-900">
                 {new Date(currentContext.updatedAt).toLocaleString()}
               </p>
             </div>
            
            <div>
              <p className="text-sm font-medium text-gray-700">Last Updated</p>
              <p className="text-gray-900">
                {new Date(currentContext.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* File Statistics */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">File Statistics</h3>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Total Files</p>
              <p className="text-2xl font-bold text-gray-900">{currentContext.ragItems?.length || 0}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-700">Protected Keywords</p>
              <p className="text-2xl font-bold text-gray-900">{currentContext.protectedKeywords?.length || 0}</p>
            </div>
            
                         <div>
               <p className="text-sm font-medium text-gray-700">Custom Information</p>
               <p className="text-2xl font-bold text-gray-900">
                 {currentContext.ragItems?.filter(item => item.ragId === 'custom_rag').length || 0}
               </p>
             </div>
          </div>
        </div>
      </div>

             {/* Retrieval Settings */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-1">
          <BarChart3 className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">Retrieval Settings</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Per-context settings that change how documents are indexed for search.
        </p>

        <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Contextual Retrieval</p>
            <p className="text-sm text-gray-600 mt-1">
              Prepends an LLM-generated context to each chunk before indexing to improve
              search recall. The original chunk text is kept verbatim for display and citation.
            </p>
            <p className="text-xs text-amber-700 mt-2">
              ⚠ Toggling re-embeds every file in this context — this calls the LLM per chunk
              and can take several minutes.
            </p>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <button
              type="button"
              onClick={handleToggleContextual}
              disabled={contextualBusy}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${currentContext.contextualRetrieval ? 'bg-blue-600' : 'bg-gray-300'} ${contextualBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${currentContext.contextualRetrieval ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-xs text-gray-600 mt-2 whitespace-nowrap">
              {contextualBusy
                ? (contextualProgress
                    ? `Re-embedding ${contextualProgress.done}/${contextualProgress.total}…`
                    : 'Queuing re-embed…')
                : currentContext.contextualRetrieval ? 'On' : 'Off'}
            </span>
          </div>
        </div>
      </div>

      {/* Retrieval Parameters — query-time knobs, shared with the Query/Ask tabs */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-1">
          <BarChart3 className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">Retrieval Parameters</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Query-time knobs used by the Query &amp; Ask tabs. Defaults track the backend
          (reranker on, cosine floor 0.60 — Korean-calibrated; English prefers ~0.55).
        </p>

        {/* Reranker toggle */}
        <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50 mb-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Cross-encoder Reranker</p>
            <p className="text-sm text-gray-600 mt-1">
              Second-stage re-ranking of the fused candidates. Large, robust quality gain
              (+6–10 nDCG@10) at some latency cost.
            </p>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <button
              type="button"
              onClick={() => setRetrievalParams({ useReranker: !retrievalParams.useReranker })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${retrievalParams.useReranker ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${retrievalParams.useReranker ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-xs text-gray-600 mt-2">{retrievalParams.useReranker ? 'On' : 'Off'}</span>
          </div>
        </div>

        {/* Numeric knobs */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cosine floor <span className="text-gray-400">(abstention gate)</span>
            </label>
            <input
              type="number" min={0} max={1} step={0.05}
              value={retrievalParams.scoreThreshold}
              onChange={(e) => setRetrievalParams({ scoreThreshold: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alpha <span className="text-gray-400">(dense ↔ BM25)</span>: {retrievalParams.alpha.toFixed(2)}
            </label>
            <input
              type="range" min={0} max={1} step={0.05}
              value={retrievalParams.alpha}
              onChange={(e) => setRetrievalParams({ alpha: parseFloat(e.target.value) })}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Top-K</label>
            <input
              type="number" min={1} max={50} step={1}
              value={retrievalParams.topK}
              onChange={(e) => setRetrievalParams({ topK: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File retrieval K</label>
            <input
              type="number" min={0} step={1}
              title="0 = no cap, use every file"
              value={retrievalParams.fileRetrievalK}
              onChange={(e) => setRetrievalParams({ fileRetrievalK: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Context window</label>
            <input
              type="number" min={0} max={5} step={1}
              value={retrievalParams.contextWindow}
              onChange={(e) => setRetrievalParams({ contextWindow: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
      </div>

      {/* Confirm: contextual retrieval toggle triggers a full re-embed */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {nextEnabled ? 'Enable' : 'Disable'} Contextual Retrieval?
            </h3>
            <p className="text-sm text-gray-600">
              This will re-embed{' '}
              <span className="font-semibold text-gray-900">
                {fileCount} {fileCount === 1 ? 'file' : 'files'}
              </span>{' '}
              in <span className="font-mono">{currentContext.contextId}</span>.
              Each chunk is sent to the LLM, so this can take several minutes and incurs cost.
              You can keep using other tabs while it runs.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runToggle}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                {nextEnabled ? 'Enable & re-embed' : 'Disable & re-embed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current RAG Items Status */}
       <div className="card">
         <h3 className="text-lg font-medium text-gray-900 mb-4">Current RAG Items Status</h3>
         
         {currentContext.ragItems && currentContext.ragItems.length > 0 ? (
           <div className="space-y-3">
             {currentContext.ragItems
               .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
               .slice(0, 5)
               .map((item) => (
               <div key={item.ragId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                 <div className="flex items-center space-x-3">
                   <FileText className="h-4 w-4 text-gray-500" />
                   <div>
                     <p className="text-sm font-medium text-gray-900">{item.fileName || 'Unknown File'}</p>
                     <p className="text-xs text-gray-500">Status: {item.status || 'Unknown'}</p>
                   </div>
                 </div>
                 <div className="text-xs text-gray-500">
                   {new Date(item.updatedAt).toLocaleString()}
                 </div>
               </div>
             ))}
             {currentContext.ragItems.length > 5 && (
               <p className="text-sm text-gray-500 text-center">
                 And {currentContext.ragItems.length - 5} more files...
               </p>
             )}
           </div>
         ) : (
           <div className="text-center py-8">
             <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
             <p className="text-gray-500">No files uploaded yet</p>
             <p className="text-sm text-gray-400">Upload files to see their status here</p>
           </div>
         )}
       </div>


    </div>
  );
};

export default ContextManagementTab;
